import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronLeft, Package, MapPin, Plus, Layers, Radio, ShoppingBag, Boxes } from 'lucide-react';
import InventoryOverview from './InventoryOverview';
import DeliveryInventoryDialog from './DeliveryInventoryDialog';
import AddProductForm from './AddProductForm';
import AddCategoryForm from './AddCategoryForm';
import AddLocationForm from './AddLocationForm';
import AddStockForm from './AddStockForm';
import ComboModal from './ComboModal';
import { PackagesInFastmax } from './PackagesInFastmax';
import { LiveDeliveriesModal } from './LiveDeliveriesModal';

function Inventory({ userId, setActiveComponent }) {
  const [inventoryData, setInventoryData] = useState({
      items: [],
      deliveries: [],
      counts: { deliveries: 0, packages: 0, units: 0 }
    });
  const [categories, setCategories] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [comboModalOpen, setComboModalOpen] = useState(false);
  const [fastmaxModalOpen, setFastmaxModalOpen] = useState(false);
  const [liveDeliveriesOpen, setLiveDeliveriesOpen] = useState(false);
  
  const [activeForm, setActiveForm] = useState(null);

  useEffect(() => {
    if (!userId) return;

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/today_packages`, {
        params: { user_id: userId }
      })
      .then(response => {
        setInventoryData(response.data)
      })
      .catch(error => {
        console.error("Error fetching today's packages:", error);
      });
  }, [userId]);

  function handleComboCreated(combo) {
    console.log('Combo created:', combo);
    // Optionally refresh data or show success message
  }

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/categories`)
      .then(res => setCategories(res.data))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${process.env.REACT_APP_API_URL}/api/locations`, { 
      params: { user_id: userId } 
    })
      .then(res => setLocationOptions(res.data))
      .catch(console.error);
  }, [userId]);

  function handleFormToggle(formName) {
    setActiveForm(activeForm === formName ? null : formName);
  }

  return (
    <div className="h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setActiveComponent('OptionsAdmin')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-slate-700" />
              </button>
              <div>
                <h1 className="text-xl font-extrabold text-[#B1336E] font-montserrat flex items-center gap-2">
                  <Package className="w-6 h-6" />
                  Inventario
                </h1>
                <p className="text-sm text-slate-600 font-montserrat">
                  Gestiona tus productos y stock
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setComboModalOpen(true)}
                className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors"
                title="Gestionar Combos"
              >
                <ShoppingBag className="w-5 h-5" />
              </button>
              <button
                onClick={() => setDialogOpen(true)}
                className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#67A43C] to-[#E6B43C] text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 font-montserrat text-sm"
              >
                <Package className="w-4 h-4" />
                <span className="hidden sm:inline">Envíos Hoy</span>
              </button>
              <button
                onClick={() => setLiveDeliveriesOpen(true)}
                className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors relative"
                title="Seguimiento en vivo"
              >
                <Radio className="w-5 h-5" />
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#67A43C] animate-pulse" />
              </button>
              <button
                onClick={() => setFastmaxModalOpen(true)}
                className="p-2 text-[#B1336E] hover:bg-[#B1336E]/10 rounded-lg transition-colors"
                title="Paquetes en Fast Max"
              >
                <Boxes className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => handleFormToggle('product')}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-200 font-montserrat ${
                activeForm === 'product'
                  ? 'bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white shadow-md'
                  : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-[#B1336E]'
              }`}
            >
              <Package className="w-4 h-4" />
              Agregar Producto
            </button>

            <button
              onClick={() => handleFormToggle('category')}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-200 font-montserrat ${
                activeForm === 'category'
                  ? 'bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white shadow-md'
                  : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-[#B1336E]'
              }`}
            >
              <Layers className="w-4 h-4" />
              Agregar Categoría
            </button>

            <button
              onClick={() => handleFormToggle('location')}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-200 font-montserrat ${
                activeForm === 'location'
                  ? 'bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white shadow-md'
                  : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-[#B1336E]'
              }`}
            >
              <MapPin className="w-4 h-4" />
              Agregar Ubicación
            </button>

            <button
              onClick={() => handleFormToggle('stock')}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-sm transition-all duration-200 font-montserrat ${
                activeForm === 'stock'
                  ? 'bg-gradient-to-r from-[#B1336E] to-[#E6B43C] text-white shadow-md'
                  : 'bg-white border-2 border-slate-200 text-slate-700 hover:border-[#B1336E]'
              }`}
            >
              <Plus className="w-4 h-4" />
              Agregar Stock
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full mx-auto px-4 py-6 sm:px-6 lg:px-8 space-y-6">
          
          {/* Forms */}
          {activeForm === 'product' && (
            <AddProductForm 
              userId={userId} 
              categories={categories}
              onSuccess={() => setActiveForm(null)}
            />
          )}

          {activeForm === 'category' && (
            <AddCategoryForm 
              onSuccess={() => setActiveForm(null)}
            />
          )}

          {activeForm === 'location' && (
            <AddLocationForm 
              userId={userId}
              onSuccess={() => setActiveForm(null)}
            />
          )}

          {activeForm === 'stock' && (
            <AddStockForm 
              userId={userId}
              locationOptions={locationOptions}
              onSuccess={() => setActiveForm(null)}
            />
          )}

          {/* Inventory Overview */}
          <InventoryOverview userId={userId} />
        </div>
      </div>

      <PackagesInFastmax
        isOpen={fastmaxModalOpen}
        onClose={() => setFastmaxModalOpen(false)}
        userId={userId}
      />   

      <LiveDeliveriesModal
        isOpen={liveDeliveriesOpen}
        onClose={() => setLiveDeliveriesOpen(false)}
        userId={userId}
      />
       
      {/* Delivery Dialog */}
      <DeliveryInventoryDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        data={inventoryData}
      />
      {comboModalOpen && (
        <ComboModal
          userId={userId}
          onClose={() => setComboModalOpen(false)}
          onComboCreated={handleComboCreated}
        />
      )}
    </div>
  );
}

export default Inventory;